/**
 * Préréglages de période (D-03) et utilitaires de dates calendaires.
 *
 * Les bornes sont des dates LOCALES `YYYY-MM-DD` du fuseau métier, toutes deux
 * incluses (contrat §1). Le préréglage actif est porté par l'état de l'écran :
 * il n'est jamais déduit de la longueur de la plage (une plage libre de 30
 * jours n'est pas « 30 derniers jours »).
 *
 * « Aujourd'hui » vient du SERVEUR (`today` de `GET /scopes`, contrat v2.2
 * §11) : ni l'horloge ni la base de fuseaux du navigateur, qui peut ignorer le
 * passage du Maroc à +00 permanent (2026-09-20). Tout ce module n'est
 * qu'arithmétique de dates calendaires (année, mois, jour) : aucun instant,
 * aucun fuseau, résultat identique quel que soit le fuseau du poste.
 */

export const PRESET_KEYS = [
    'last7',
    'last30',
    'last90',
    'last365',
    'currentMonth',
    'previousMonth',
    'last12FullMonths',
    'schoolYear',
] as const;
export type PresetKey = (typeof PRESET_KEYS)[number];

export type DateRange = { from: string; to: string };

type Ymd = { y: number; m: number; d: number };

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (n: number, width = 2) => String(n).padStart(width, '0');
const toIso = ({ y, m, d }: Ymd): string => `${pad(y, 4)}-${pad(m)}-${pad(d)}`;

/** Jours du mois `m` (1-12) de l'année `y` (calendrier grégorien, en UTC : sans fuseau). */
const daysInMonth = (y: number, m: number): number => new Date(Date.UTC(y, m, 0)).getUTCDate();

const parseIso = (value: string): Ymd | null => {
    const match = ISO_DATE.exec(value);
    if (!match) return null;
    const ymd = { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
    if (ymd.m < 1 || ymd.m > 12 || ymd.d < 1 || ymd.d > daysInMonth(ymd.y, ymd.m)) return null;
    return ymd;
};

export const isIsoDate = (value: string | null | undefined): value is string => !!value && parseIso(value) !== null;

const mustParse = (value: string): Ymd => {
    const ymd = parseIso(value);
    if (!ymd) throw new Error(`[monitoring] date du jour invalide : ${value}`);
    return ymd;
};

/** Décale une date calendaire de `n` jours (arithmétique en UTC : sans fuseau). */
export const addDays = (value: string, n: number): string => {
    const { y, m, d } = mustParse(value);
    const t = new Date(Date.UTC(y, m - 1, d + n));
    return toIso({ y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() });
};

/** Mois décalé de `n` (année et mois seulement). */
const shiftMonth = (y: number, m: number, n: number): { y: number; m: number } => {
    const k = y * 12 + (m - 1) + n;
    return { y: Math.floor(k / 12), m: (((k % 12) + 12) % 12) + 1 };
};

const monthRange = (y: number, m: number): DateRange => ({
    from: toIso({ y, m, d: 1 }),
    to: toIso({ y, m, d: daysInMonth(y, m) }),
});

const lastDays = (today: string, days: number): DateRange => ({ from: addDays(today, -(days - 1)), to: today });

/** Bornes d'un préréglage pour la date du jour `today` (`YYYY-MM-DD`, fournie par le serveur). */
export const computePreset = (key: PresetKey, today: string): DateRange => {
    const { y, m } = mustParse(today);
    switch (key) {
        case 'last7':
            return lastDays(today, 7);
        case 'last30':
            return lastDays(today, 30);
        case 'last90':
            return lastDays(today, 90);
        case 'last365':
            // Glissant : 365 jours, aujourd'hui compris.
            return lastDays(today, 365);
        case 'currentMonth':
            // Mois entier, fin comprise même si elle est future (D-01).
            return monthRange(y, m);
        case 'previousMonth': {
            const prev = shiftMonth(y, m, -1);
            return monthRange(prev.y, prev.m);
        }
        case 'last12FullMonths': {
            // Les 12 derniers mois calendaires COMPLETS : le mois en cours exclu.
            const first = shiftMonth(y, m, -12);
            const last = shiftMonth(y, m, -1);
            return { from: monthRange(first.y, first.m).from, to: monthRange(last.y, last.m).to };
        }
        case 'schoolYear': {
            // Année scolaire en cours, de septembre à août.
            const startYear = m >= 9 ? y : y - 1;
            return { from: `${startYear}-09-01`, to: `${startYear + 1}-08-31` };
        }
        default: {
            const exhaustive: never = key;
            throw new Error(`Préréglage inconnu : ${String(exhaustive)}`);
        }
    }
};

export const DEFAULT_PRESET: PresetKey = 'last30';
